import { useEffect, useRef, useState } from 'react'
import Chart from 'chart.js/auto'
import { useApp } from '../../../store.jsx'
import { Icon, StatCard, Avatar } from '../../../components/Common.jsx'
import ProjectsAtRiskCard from '../../../components/projects/ProjectsAtRiskCard.jsx'
import AnnouncementBanner from '../../../components/announcements/AnnouncementBanner.jsx'
import { fmt, greeting } from '../../../data.js'
import { ReportModal, SubmitReportModal } from '../../../components/Modals.jsx'
import {
  getAnalytics,
  sendReminder as apiSendReminder,
  remindAllPending,
  downloadAnalyticsExport,
  getReportById,
  getReportDraft,
  listReports,
} from '../../../lib/api'
import { useSession } from '../../../lib/auth-client'

const cssVar = (n) => getComputedStyle(document.documentElement).getPropertyValue(n).trim()

const dayStr = (d) => new Date(d).toISOString().slice(0, 10)
const computeStreak = (reports) => {
  if (!reports.length) return 0
  const days = new Set(reports.map((r) => dayStr(r.date)))
  let s = 0
  const d = new Date()
  while (days.has(dayStr(d))) {
    s++
    d.setDate(d.getDate() - 1)
  }
  return s
}

function chartOpts(opts = {}) {
  const tick = cssVar('--tick-color') || '#6B7280'
  const grid = cssVar('--grid-line') || 'rgba(38,38,54,.6)'
  const tipBg = cssVar('--tooltip-bg') || '#14141C'
  const muted = cssVar('--text-muted') || '#94A3B8'
  const text = cssVar('--text') || '#F1F5F9'
  const border = cssVar('--border') || '#262636'
  const isLight = document.documentElement.dataset.theme === 'light'
  return {
    responsive: true, maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: opts.legend ? { position: 'top', align: 'end', labels: { color: muted, boxWidth: 10, boxHeight: 10, padding: 14, font: { size: 12 } } } : { display: false },
      tooltip: { backgroundColor: tipBg, borderColor: border, borderWidth: 1, padding: 12, titleColor: isLight ? '#FFFFFF' : text, bodyColor: isLight ? '#CBD5E1' : muted }
    },
    scales: {
      x: { grid: { display: false }, ticks: { color: tick, font: { size: 11 } } },
      y: { grid: { color: grid }, ticks: { color: tick, font: { size: 11 } }, beginAtZero: true }
    }
  }
}

const reasonText = (r) => ({
  phone_not_verified: 'Phone not verified — reminder skipped',
  throttled: 'Already reminded recently — try again later',
  send_failed: 'WhatsApp send failed',
}[r] || 'Could not send reminder')

export default function ManagerDashboard() {
  const { state, toast, openModal, closeModal, goPage } = useApp()
  const { data: session } = useSession()
  const firstName = (session?.user?.name || state.user.name || 'there').split(' ')[0]
  const [period, setPeriod] = useState('daily')
  const [range, setRange] = useState(30)
  const [customFrom, setCustomFrom] = useState("")
  const [customTo, setCustomTo] = useState("")
  const actRef = useRef(null), statRef = useRef(null)
  const actChart = useRef(null), statChart = useRef(null)

  // Live data from the manager analytics endpoint.
  const [live, setLive] = useState(null)
  const [, setLoading] = useState(true)
  useEffect(() => {
    let cancelled = false
    setLoading(true)
    if (customFrom && customTo) {
      getAnalytics("", customFrom, customTo)
        .then((d) => { if (!cancelled) setLive(d) })
        .catch(() => { if (!cancelled) setLive(null) })
        .finally(() => { if (!cancelled) setLoading(false) })
    } else {
      getAnalytics(`${range}d`)
        .then((d) => { if (!cancelled) setLive(d) })
        .catch(() => { if (!cancelled) setLive(null) })
        .finally(() => { if (!cancelled) setLoading(false) })
    }
    return () => { cancelled = true }
  }, [range, customFrom, customTo])

  // ─── Personal "My day" — managers file their own daily reports too ───────
  const [myReports, setMyReports] = useState([])
  const [myDraft, setMyDraft] = useState({ items: [], loading: true })
  const loadMine = () =>
    listReports({ scope: 'mine', pageSize: 60 })
      .then((r) => setMyReports(r?.reports || []))
      .catch(() => {})
  useEffect(() => {
    loadMine()
    getReportDraft()
      .then(({ items = [] }) => setMyDraft({ items, loading: false }))
      .catch(() => setMyDraft({ items: [], loading: false }))
  }, [])
  const todayKey = new Date().toISOString().slice(0, 10)
  const submittedToday = myReports.some((r) => dayStr(r.date) === todayKey)
  const myStreak = computeStreak(myReports)
  const myThisMonth = (() => {
    const m = new Date(); m.setDate(1); m.setHours(0, 0, 0, 0)
    return myReports.filter((r) => new Date(r.date) >= m).length
  })()
  const submitMyReport = () =>
    openModal(
      <SubmitReportModal
        onClose={closeModal}
        onSubmit={async () => {
          toast('Report submitted', 'success')
          await loadMine()
          getReportDraft()
            .then(({ items = [] }) => setMyDraft({ items, loading: false }))
            .catch(() => {})
        }}
      />,
      'md',
    )

  const teamToday = live?.teamToday || {
    activeTeam: 0, reportsToday: 0, pending: 0, compliance: 0,
    taskBreakdown: { done: 0, in_process: 0, pending: 0 },
    pendingEmployees: [], onLeaveEmployees: [], topPerformers: [], recentFeed: [],
  }
  const taskBreakdown = teamToday.taskBreakdown
  const activeTeam = teamToday.activeTeam
  const pending = teamToday.pending
  const compliance = teamToday.compliance
  const todaysTeamCount = teamToday.reportsToday

  useEffect(() => {
    if (!actRef.current) return
    if (actChart.current) actChart.current.destroy()
    const trend = live?.dailyTrend || []

    const buckets = []
    const push = (key, label, d) => {
      const last = buckets[buckets.length - 1]
      if (last && last.key === key) {
        last.submitted += d.submitted
        last.expected += d.expected
      } else {
        buckets.push({ key, label, submitted: d.submitted, expected: d.expected })
      }
    }
    for (const d of trend) {
      const dt = new Date(d.date)
      if (period === 'monthly') {
        const key = `${dt.getUTCFullYear()}-${dt.getUTCMonth()}`
        const label = dt.toLocaleDateString('en-GB', { month: 'short', year: '2-digit' })
        push(key, label, d)
      } else if (period === 'weekly') {
        const day = new Date(Date.UTC(dt.getUTCFullYear(), dt.getUTCMonth(), dt.getUTCDate()))
        const dow = day.getUTCDay() || 7 // Mon=1..Sun=7
        day.setUTCDate(day.getUTCDate() - (dow - 1)) // back to Monday
        const key = day.toISOString().slice(0, 10)
        const label = `Wk of ${day.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}`
        push(key, label, d)
      } else {
        push(d.date, dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }), d)
      }
    }

    const labels = buckets.map((b) => b.label)
    const submitted = buckets.map((b) => b.submitted)
    const expected = buckets.map((b) => b.expected)

    actChart.current = new Chart(actRef.current, {
      type: 'line',
      data: { labels, datasets: [
        { label: 'My team submitted', data: submitted, borderColor: '#8B5CF6', backgroundColor: ctx => { const g = ctx.chart.ctx.createLinearGradient(0, 0, 0, 280); g.addColorStop(0, 'rgba(139,92,246,.4)'); g.addColorStop(1, 'rgba(139,92,246,0)'); return g }, fill: true, tension: .4, borderWidth: 2.5, pointRadius: 0, pointHoverRadius: 5 },
        { label: 'Expected', data: expected, borderColor: 'rgba(148,163,184,.4)', borderDash: [4, 4], borderWidth: 1.5, pointRadius: 0, fill: false }
      ]},
      options: chartOpts({ legend: true })
    })
    return () => { actChart.current?.destroy() }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live, period, state.settings.theme])

  useEffect(() => {
    if (!statRef.current) return
    if (statChart.current) statChart.current.destroy()
    statChart.current = new Chart(statRef.current, {
      type: 'doughnut',
      data: { labels: ['Submitted', 'Pending'], datasets: [{ data: [todaysTeamCount, pending], backgroundColor: ['#10B981', '#EF4444'], borderWidth: 0 }] },
      options: { responsive: true, maintainAspectRatio: false, cutout: '70%', layout: { padding: 6 }, plugins: { legend: { position: 'bottom', align: 'center', labels: { color: cssVar('--text-muted') || '#94A3B8', padding: 10, font: { size: 11 }, boxWidth: 10, boxHeight: 10, usePointStyle: true, pointStyle: 'circle' } } } }
    })
    return () => { statChart.current?.destroy() }
  }, [todaysTeamCount, pending, state.settings.theme])

  useEffect(() => {
    let t
    const onResize = () => { clearTimeout(t); t = setTimeout(() => { actChart.current?.resize(); statChart.current?.resize() }, 150) }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  const top = teamToday.topPerformers
  const pendingEmps = teamToday.pendingEmployees
  const pendingWindow = teamToday.pendingWindow || {}
  const onLeaveEmps = teamToday.onLeaveEmployees || []
  const feed = teamToday.recentFeed

  const [reminding, setReminding] = useState({})

  const sendReminder = async (id) => {
    const e = pendingEmps.find((x) => x.id === id)
    setReminding((m) => ({ ...m, [id]: true }))
    try {
      const { sent, failed } = await apiSendReminder(id)
      if (sent?.length) toast(`Reminder sent to ${e?.name || 'employee'} via WhatsApp`, 'success')
      else toast(reasonText(failed?.[0]?.reason), 'error')
    } catch (err) {
      toast(err.message || 'Failed to send reminder', 'error')
    } finally {
      setReminding((m) => ({ ...m, [id]: false }))
    }
  }

  const remindAll = async () => {
    if (!pendingEmps.length) { toast('No pending submissions in your team', 'info'); return }
    try {
      const { sent, failed } = await remindAllPending()
      const msg = `Sent ${sent?.length || 0} reminders` + (failed?.length ? `, ${failed.length} failed` : '')
      toast(msg, sent?.length ? 'success' : 'error')
    } catch (err) {
      toast(err.message || 'Failed to send reminders', 'error')
    }
  }

  const viewReport = async (feedItem) => {
    try {
      const { report: r } = await getReportById(feedItem.id)
      openModal(
        <ReportModal
          report={{
            ...r,
            employee: r.user,
            tasks: (r.reportTasks || []).length
              ? r.reportTasks.map((rt) => ({
                  text: rt.task?.title || '(task)',
                  status: rt.status,
                  note: rt.note,
                  source: rt.task?.source,
                  carried: !!rt.carriedFrom,
                }))
              : [{ text: r.message || '', status: 'done' }],
            blockers: (r.reportTasks || [])
              .filter((rt) => rt.status === 'pending' && rt.note)
              .map((rt) => rt.note),
            aiSummary: r.analysis?.summary || 'AI summary pending...',
            sentiment: r.analysis?.sentiment || 'neutral',
            source: r.source || 'web',
            hoursWorked: 0,
          }}
          onClose={closeModal}
        />,
        'lg',
      )
    } catch (err) {
      toast(err.message || 'Could not load report', 'error')
    }
  }

  const exportData = async () => {
    try {
      toast('Preparing export…', 'info')
      if (customFrom && customTo) {
        await downloadAnalyticsExport("", customFrom, customTo)
      } else {
        await downloadAnalyticsExport(`${range}d`)
      }
      toast('Export downloaded', 'success')
    } catch (err) {
      toast(err.message || 'Export failed', 'error')
    }
  }

  const openDateMenu = () =>
    openModal(
      <>
        <div className="modal-head">
          <h3>Date range</h3>
          <button className="icon-btn" onClick={closeModal}><Icon name="x" size={16} /></button>
        </div>
        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {[7, 14, 30, 60, 90].map((d) => (
            <button key={d} className={`side-link ${range === d ? 'active' : ''}`} onClick={() => { setRange(d); closeModal(); toast(`Showing last ${d} days`, 'info') }}>
              <Icon name="calendar" size={18} /> Last {d} days
            </button>
          ))}
        </div>
      </>,
    )

  return (
    <section>
      <AnnouncementBanner placement="dashboard" />
      <div className="page-head">
        <div>
          <h1 className="page-title">{greeting()}, {firstName} 👋</h1>
          <p className="page-sub">Your team overview for {fmt.date(new Date())}</p>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <button className="btn btn-ghost btn-sm" onClick={() => { setCustomFrom(""); setCustomTo(""); openDateMenu(); }}><Icon name="calendar" size={16} /> {customFrom && customTo ? "Custom" : `Last ${range}d`}</button>
          <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)}
            style={{ fontSize: 12, padding: "4px 8px", border: "1px solid var(--border)", borderRadius: 6, background: "var(--surface)", color: "var(--text)", flex: "1 1 120px", minWidth: 0, maxWidth: 150 }} />
          <span style={{ fontSize: 12, color: "var(--text-muted)" }}>to</span>
          <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)}
            style={{ fontSize: 12, padding: "4px 8px", border: "1px solid var(--border)", borderRadius: 6, background: "var(--surface)", color: "var(--text)", flex: "1 1 120px", minWidth: 0, maxWidth: 150 }} />
          {(customFrom || customTo) && (
            <button className="btn btn-ghost btn-sm" style={{ fontSize: 11, padding: "2px 8px" }} onClick={() => { setCustomFrom(""); setCustomTo(""); }}>Clear</button>
          )}
          <button className="btn btn-ghost btn-sm" onClick={remindAll}><Icon name="bell" size={16} /> Remind all pending</button>
          <button className="btn btn-ghost btn-sm" onClick={exportData}><Icon name="download" size={16} /> Export</button>
        </div>
      </div>

      {/* Personal: managers file their own daily reports, same as employees */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header">
          <div>
            <div className="card-title">My day</div>
            <div className="card-subtitle">{submittedToday ? "You've submitted your report today" : 'Submit your own daily report'}</div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button className="btn btn-ghost btn-sm" onClick={() => goPage('myTasks')}><Icon name="check-square" size={16} /> My Tasks</button>
            <button className="btn btn-primary btn-sm" onClick={submitMyReport}><Icon name="send" size={16} /> {submittedToday ? 'Submit another' : "Submit today's report"}</button>
          </div>
        </div>
        <div className="stats-row stats-row-3" style={{ marginTop: 4 }}>
          <StatCard label="My tasks for today" value={fmt.num(myDraft.items.length)} icon="check-square" color="primary" />
          <StatCard label="My reports this month" value={fmt.num(myThisMonth)} icon="file-text" color="blue" />
          <StatCard label="My streak" value={`${myStreak}d`} icon="calendar" color="amber" />
        </div>
        <div style={{ marginTop: 12 }}>
          <div className="card-subtitle" style={{ marginBottom: 6 }}>My recent reports</div>
          {myReports.length ? myReports.slice(0, 5).map((r) => (
            <div key={r.id} className="activity-row">
              <div className="activity-text">
                <div className="activity-title" style={{ fontWeight: 600 }}>{fmt.date(new Date(r.date))}</div>
                <div className="activity-meta">{(r.reportTasks?.length || 0)} tasks · via {r.source === 'whatsapp' ? 'WhatsApp' : 'Web'}</div>
              </div>
              <button className="btn btn-ghost btn-sm activity-view" onClick={() => viewReport({ id: r.id })}>View</button>
            </div>
          )) : (
            <div className="empty-state">
              <div className="ico"><Icon name="file-text" size={24} /></div>
              <h3>No reports yet</h3>
              <p>Submit your first daily report above.</p>
            </div>
          )}
        </div>
      </div>

      <div className="stats-row stats-row-4">
        <StatCard label="My Team Size" value={fmt.num(activeTeam)} icon="users" color="primary" />
        <StatCard label="Reports Today" value={fmt.num(todaysTeamCount)} icon="file-text" color="blue" />
        <StatCard label="Pending" value={fmt.num(pending)} icon="clock" color="amber" />
        <StatCard label="Team Compliance" value={compliance + '%'} icon="shield-check" color="green" />
      </div>

      <ProjectsAtRiskCard />

      <div className="card" style={{ padding: 16, marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div className="card-title">Today's task breakdown</div>
            <div className="card-subtitle">Across all reports submitted today</div>
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <span className="badge badge-success">Done · {taskBreakdown.done}</span>
            <span className="badge badge-muted">In Process · {taskBreakdown.in_process}</span>
            <span className="badge badge-error">Pending · {taskBreakdown.pending}</span>
          </div>
        </div>
      </div>

      <div className="grid-2">
        <div className="card">
          <div className="card-header">
            <div><div className="card-title">My team's activity</div><div className="card-subtitle">Submissions over time</div></div>
            <div style={{ display: 'flex', gap: 6 }}>
              {['daily', 'weekly', 'monthly'].map(p => <button key={p} className={`filter-chip ${period === p ? 'active' : ''}`} onClick={() => setPeriod(p)}>{p[0].toUpperCase() + p.slice(1)}</button>)}
            </div>
          </div>
          <div className="chart-wrap"><canvas ref={actRef} /></div>
        </div>
        <div className="card">
          <div className="card-header"><div><div className="card-title">Submission status</div><div className="card-subtitle">My team today</div></div></div>
          <div className="chart-wrap"><canvas ref={statRef} /></div>
        </div>
      </div>

      <div className="grid-3">
        <div className="card">
          <div className="card-header"><div><div className="card-title">Top performers in my team</div><div className="card-subtitle">By report quality this week</div></div><button type="button" onClick={() => goPage('employees')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--primary-2)', fontSize: 13, fontWeight: 600 }}>View all →</button></div>
          <div className="list-scroll">
          {top.map(e => (
            <div key={e.id} className="activity-row">
              <Avatar name={e.name} seed={e.id.length + e.name.length} />
              <div className="activity-text"><div className="activity-title" style={{ fontWeight: 600 }}>{e.name}</div><div className="activity-meta">{e.department || '—'} · {e.reportsCount} reports</div></div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}><div style={{ color: 'var(--success)', fontWeight: 700, fontSize: 14 }}>{e.compliance}%</div><div style={{ fontSize: 11, color: 'var(--text-muted)' }}>compliance</div></div>
            </div>
          ))}
          </div>
        </div>
        <div className="card">
          <div className="card-header"><div><div className="card-title">Pending submissions</div><div className="card-subtitle">{pendingWindow.active === false ? `My team · report due at ${pendingWindow.eveningTime || '18:00'}` : 'My team · missed the evening report'}</div></div></div>
          {pendingEmps.length ? (
            <div className="list-scroll">
            {pendingEmps.map(e => (
            <div key={e.id} className="activity-row">
              <Avatar name={e.name} seed={e.id.length + 5} />
              <div className="activity-text"><div className="activity-title" style={{ fontWeight: 600 }}>{e.name}</div><div className="activity-meta">{e.department || '—'}</div></div>
              <button
                className="btn btn-ghost btn-sm activity-view"
                disabled={!!reminding[e.id]}
                onClick={() => sendReminder(e.id)}
              >
                <Icon name="bell" size={14} /> {reminding[e.id] ? 'Sending…' : 'Remind'}
              </button>
            </div>
            ))}
            </div>
          ) : (
            <div className="empty-state">
              <div className="ico"><Icon name={pendingWindow.active === false ? 'clock' : 'check-circle'} size={28} /></div>
              {pendingWindow.active === false ? (
                <>
                  <h3>Report not due yet</h3>
                  <p>The evening prompt goes out at {pendingWindow.eveningTime || '18:00'}. Anyone who misses it shows here.</p>
                </>
              ) : (
                <>
                  <h3>All caught up!</h3>
                  <p>Everyone who got the evening prompt has submitted.</p>
                </>
              )}
            </div>
          )}
        </div>
        <div className="card">
          <div className="card-header"><div><div className="card-title">On leave today</div><div className="card-subtitle">Skipped via WhatsApp</div></div></div>
          {onLeaveEmps.length ? (
            <div className="list-scroll">
            {onLeaveEmps.map(e => (
            <div key={e.id} className="activity-row">
              <Avatar name={e.name} seed={e.id.length + 3} />
              <div className="activity-text"><div className="activity-title" style={{ fontWeight: 600 }}>{e.name}</div><div className="activity-meta">{e.department || '—'}</div></div>
              <span className="badge badge-error" style={{ flexShrink: 0 }}>On Leave</span>
            </div>
            ))}
            </div>
          ) : (
            <div className="empty-state">
              <div className="ico"><Icon name="check-circle" size={28} /></div>
              <h3>No leaves today</h3>
              <p>Everyone is available.</p>
            </div>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-header"><div><div className="card-title">My team's recent activity</div><div className="card-subtitle">Live feed of submissions</div></div></div>
        <div className="list-scroll">
        {feed.map(r => (
          <div key={r.id} className="activity-row">
            <Avatar name={r.employee.name} size="sm" seed={r.employee.id.length} />
            <div className="activity-text">
              <div className="activity-title"><span style={{ fontWeight: 600 }}>{r.employee.name}</span> submitted via <span style={{ color: 'var(--primary-2)' }}>{r.source === 'whatsapp' ? 'WhatsApp' : 'Web'}</span></div>
              <div className="activity-meta">{r.taskCount} tasks · {fmt.rel(new Date(r.submittedAt))}</div>
            </div>
            <button className="btn btn-ghost btn-sm activity-view" onClick={() => viewReport(r)}>View</button>
          </div>
        ))}
        </div>
      </div>
    </section>
  )
}
